import React from 'react';
import { Container, Card, Row, Col, Form, Button, Alert, Badge, Table } from 'react-bootstrap';
import { PlusIcon, TrashIcon, CalculatorIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import CompanyLayout from '../common/CompanyLayout';
import { useCostCalculator } from './hooks/useCostCalculator';

const sectionLabel: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#9ca3af',
  marginBottom: '14px',
  paddingBottom: '8px',
  borderBottom: '1px solid #f1f5f9',
};

const statCard = (value: string, label: string, color = '#059669') => (
  <Card className="border-0 shadow-sm text-center" style={{ borderRadius: '10px' }}>
    <Card.Body className="py-3 px-2">
      <div style={{ fontSize: '1.35rem', fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 500 }}>{label}</div>
    </Card.Body>
  </Card>
);

export default function CostCalculator() {
  const {
    recipes, loadingRecipes,
    items, overheadPct, setOverheadPct,
    addItem, removeItem, updateItem,
    result, loading, error,
    expandedRecipe, setExpandedRecipe,
    calculate, fmt,
  } = useCostCalculator();

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-start mb-4">
          <div>
            <h4 style={{ fontWeight: 800, color: '#1e293b', marginBottom: '4px' }}>
              <CalculatorIcon style={{ width: 22, height: 22, marginRight: 8, color: '#059669', verticalAlign: 'middle' }} />
              Cost Calculator
            </h4>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
              Calculate ingredient costs, overhead, and suggested selling prices per recipe.
            </p>
          </div>
        </div>

        <Row className="g-4">
          {/* LEFT — Setup */}
          <Col lg={5}>
            {/* Recipes */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '14px' }}>
              <Card.Body className="p-4">
                <div style={sectionLabel}>RECIPES TO COST</div>

                {items.length === 0 && (
                  <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Add a recipe to get started.</p>
                )}

                {items.map(item => (
                  <Row key={item._key} className="g-2 mb-2 align-items-center">
                    <Col xs={6}>
                      <Form.Select
                        size="sm"
                        value={item.recipe_id || ''}
                        onChange={e => updateItem(item._key, 'recipe_id', parseInt(e.target.value))}
                      >
                        <option value="">— pick recipe —</option>
                        {recipes.map((r: any) => (
                          <option key={r.id} value={r.id}>
                            {r.name}{r.yield_quantity ? ` (makes ${r.yield_quantity} ${r.yield_unit})` : ''}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col xs={3}>
                      <Form.Control
                        size="sm"
                        type="number"
                        min={1}
                        value={item.quantity}
                        onChange={e => updateItem(item._key, 'quantity', parseInt(e.target.value) || 1)}
                        placeholder="Qty"
                      />
                    </Col>
                    <Col xs={2}>
                      <div className="d-flex align-items-center gap-1">
                        <Form.Control
                          size="sm"
                          type="number"
                          min={0}
                          max={99}
                          value={item.margin_pct}
                          onChange={e => updateItem(item._key, 'margin_pct', parseFloat(e.target.value) || 0)}
                          placeholder="Margin%"
                          title="Target margin %"
                        />
                        <span style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>%</span>
                      </div>
                    </Col>
                    <Col xs={1}>
                      <Button
                        variant="link"
                        className="p-0"
                        onClick={() => removeItem(item._key)}
                        style={{ color: '#ef4444' }}
                      >
                        <TrashIcon style={{ width: 16, height: 16 }} />
                      </Button>
                    </Col>
                  </Row>
                ))}

                {items.length > 0 && (
                  <div className="d-flex gap-2 mt-1 mb-1" style={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                    <span style={{ flex: '0 0 50%' }}>Recipe</span>
                    <span style={{ flex: '0 0 25%' }}>Quantity</span>
                    <span style={{ flex: '0 0 20%' }}>Margin %</span>
                  </div>
                )}

                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mt-2 w-100"
                  onClick={addItem}
                  disabled={loadingRecipes}
                >
                  <PlusIcon style={{ width: 14, height: 14, marginRight: 4 }} />
                  Add Recipe
                </Button>
              </Card.Body>
            </Card>

            {/* Overhead */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '14px' }}>
              <Card.Body className="p-4">
                <div style={sectionLabel}>OVERHEAD & SETTINGS</div>
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                      Overhead % <span style={{ fontWeight: 400, color: '#9ca3af' }}>(packaging, energy, labour…)</span>
                    </Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Range
                        min={0} max={50} step={1}
                        value={overheadPct}
                        onChange={e => setOverheadPct(parseInt(e.target.value))}
                        style={{ flex: 1 }}
                      />
                      <span style={{ minWidth: '40px', fontWeight: 700, color: '#059669' }}>{overheadPct}%</span>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {error && <Alert variant="danger" className="mb-3" style={{ borderRadius: '12px' }}>{error}</Alert>}

            <Button
              variant="success"
              className="w-100 py-3"
              style={{ borderRadius: '12px', fontWeight: 700, fontSize: '1rem', background: '#059669', border: 'none' }}
              onClick={calculate}
              disabled={loading || items.filter(i => i.recipe_id > 0).length === 0}
            >
              <CalculatorIcon style={{ width: 18, height: 18, marginRight: 8 }} />
              {loading ? 'Calculating…' : 'Calculate Costs'}
            </Button>
          </Col>

          {/* RIGHT — Results */}
          <Col lg={7}>
            {!result && !loading && (
              <Card className="border-0 shadow-sm h-100 d-flex align-items-center justify-content-center" style={{ borderRadius: '14px', minHeight: '300px' }}>
                <Card.Body className="text-center py-5">
                  <CalculatorIcon style={{ width: 48, height: 48, color: '#e2e8f0', marginBottom: 16 }} />
                  <p style={{ color: '#94a3b8', fontWeight: 500 }}>Add recipes and click Calculate Costs</p>
                  <p style={{ color: '#cbd5e1', fontSize: '0.82rem' }}>Make sure ingredients have a cost per unit set in the Ingredients page.</p>
                </Card.Body>
              </Card>
            )}

            {result && (
              <>
                {/* Grand total summary */}
                <Row className="g-3 mb-3">
                  {statCard(`$${fmt(result.grand_total_cost)}`, 'Total Ingredient Cost')}
                  {result.results.map(r => (
                    <React.Fragment key={r.recipe_id}>
                      {statCard(`$${fmt(r.cost_per_piece)}`, `Cost / ${r.yield_unit || 'piece'}`, '#0891b2')}
                      {statCard(`$${fmt(r.selling_price_per_piece)}`, `Sell @ ${r.margin_pct}% margin`, '#7c3aed')}
                      {statCard(`$${fmt(r.total_profit)}`, 'Est. Profit', r.total_profit >= 0 ? '#059669' : '#ef4444')}
                    </React.Fragment>
                  ))}
                </Row>

                {/* Per-recipe breakdown */}
                {result.results.map(r => (
                  <Card key={r.recipe_id} className="border-0 shadow-sm mb-3" style={{ borderRadius: '14px' }}>
                    <Card.Body className="p-0">
                      {/* Recipe header — clickable to expand */}
                      <div
                        className="d-flex justify-content-between align-items-center p-3"
                        style={{ cursor: 'pointer', borderBottom: expandedRecipe === r.recipe_id ? '1px solid #f1f5f9' : 'none' }}
                        onClick={() => setExpandedRecipe(expandedRecipe === r.recipe_id ? null : r.recipe_id)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {r.costing_complete
                            ? <CheckCircleIcon style={{ width: 18, height: 18, color: '#059669' }} />
                            : <ExclamationTriangleIcon style={{ width: 18, height: 18, color: '#f59e0b' }} />
                          }
                          <span style={{ fontWeight: 700, color: '#1e293b' }}>{r.recipe_name}</span>
                          <Badge bg="" style={{ background: '#f1f5f9', color: '#475569', fontSize: '0.72rem' }}>
                            {r.total_pieces} {r.yield_unit !== 'piece' ? r.yield_unit : (r.total_pieces === 1 ? 'unit' : 'units')}
                          </Badge>
                          {!r.costing_complete && (
                            <Badge bg="warning" text="dark" style={{ fontSize: '0.7rem' }}>
                              Missing costs
                            </Badge>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <span style={{ fontWeight: 700, color: '#059669' }}>${fmt(r.cost_per_piece)}<span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '0.78rem' }}>/{r.yield_unit}</span></span>
                          <span style={{ fontWeight: 700, color: '#7c3aed' }}>${fmt(r.selling_price_per_piece)}</span>
                          {expandedRecipe === r.recipe_id
                            ? <ChevronUpIcon style={{ width: 16, height: 16, color: '#94a3b8' }} />
                            : <ChevronDownIcon style={{ width: 16, height: 16, color: '#94a3b8' }} />
                          }
                        </div>
                      </div>

                      {expandedRecipe === r.recipe_id && (
                        <div className="p-3">
                          {/* Cost summary row */}
                          <Row className="g-2 mb-3">
                            {[
                              { label: 'Ingredient cost/batch', value: `$${fmt(r.ingredient_cost_per_batch)}` },
                              { label: `Overhead (${r.overhead_pct}%)`, value: `$${fmt(r.overhead_per_batch)}` },
                              { label: 'Total cost/batch', value: `$${fmt(r.cost_per_batch)}`, bold: true },
                              { label: `Cost/${r.yield_unit}`, value: `$${fmt(r.cost_per_piece)}`, bold: true },
                              { label: `Sell @ ${r.margin_pct}%`, value: `$${fmt(r.selling_price_per_piece)}`, color: '#7c3aed' },
                              { label: `Profit/${r.yield_unit}`, value: `$${fmt(r.profit_per_piece)}`, color: r.profit_per_piece >= 0 ? '#059669' : '#ef4444' },
                            ].map(({ label, value, bold, color }) => (
                              <Col xs={6} sm={4} key={label}>
                                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 12px' }}>
                                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: '2px' }}>{label}</div>
                                  <div style={{ fontWeight: bold ? 700 : 600, color: color || '#1e293b', fontSize: '0.95rem' }}>{value}</div>
                                </div>
                              </Col>
                            ))}
                          </Row>

                          {/* Ingredient line costs */}
                          <Table size="sm" className="mb-0" style={{ fontSize: '0.82rem' }}>
                            <thead>
                              <tr style={{ background: '#f8fafc' }}>
                                <th style={{ fontWeight: 600, color: '#475569', padding: '8px 12px' }}>Ingredient</th>
                                <th style={{ fontWeight: 600, color: '#475569', padding: '8px 12px' }}>Qty used</th>
                                <th style={{ fontWeight: 600, color: '#475569', padding: '8px 12px' }}>Cost/unit</th>
                                <th style={{ fontWeight: 600, color: '#475569', padding: '8px 12px', textAlign: 'right' }}>Line total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.line_costs.map((lc: any, idx: number) => (
                                <tr key={idx} style={{ borderTop: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '8px 12px', color: '#374151' }}>{lc.name}</td>
                                  <td style={{ padding: '8px 12px', color: '#64748b' }}>
                                    {lc.quantity} {lc.unit}
                                    {lc.has_cost && lc.unit !== lc.ingredient_unit && lc.converted_qty != null && (
                                      <span style={{ color: '#94a3b8', fontSize: '0.75rem', marginLeft: 4 }}>
                                        → {fmt(lc.converted_qty, 4)} {lc.ingredient_unit}
                                      </span>
                                    )}
                                    {lc.unit_mismatch && (
                                      <span style={{ color: '#ef4444', fontSize: '0.75rem', marginLeft: 4 }}>⚠ unit mismatch</span>
                                    )}
                                  </td>
                                  <td style={{ padding: '8px 12px', color: lc.has_cost ? '#374151' : lc.unit_mismatch ? '#ef4444' : '#f59e0b' }}>
                                    {lc.has_cost
                                      ? `$${fmt(lc.cost_per_unit, 4)}/${lc.ingredient_unit}`
                                      : lc.unit_mismatch
                                        ? <span style={{ fontSize: '0.75rem' }}>⚠ unit mismatch ({lc.unit} ≠ {lc.ingredient_unit})</span>
                                        : <span style={{ fontSize: '0.75rem' }}>⚠ no cost set</span>}
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: lc.has_cost ? '#059669' : '#94a3b8' }}>
                                    {lc.has_cost ? `$${fmt(lc.line_total)}` : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>

                          {r.missing_cost_ingredients.length > 0 && (
                            <Alert variant="warning" className="mt-3 mb-0 py-2 px-3" style={{ fontSize: '0.82rem', borderRadius: '8px' }}>
                              <strong>Missing costs:</strong> {r.missing_cost_ingredients.join(', ')} — set a cost per unit in Ingredients to include them.
                            </Alert>
                          )}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ))}
              </>
            )}
          </Col>
        </Row>
      </Container>
    </CompanyLayout>
  );
}
